"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { tierUnlockView, useCurrentTier, useTierLadder, useTierProgress } from "@/lib/api/incentives";
import { useBankrolls } from "@/lib/api/transfers";
import { fullNumber } from "@/lib/level";
import { MAX_BODY_LENGTH } from "@/lib/chat/config";
import { DEFAULT_ROOM } from "@/lib/chat/rooms";
import { useChat, type ChatRow as Row } from "@/lib/chat/use-chat";
import { usePlatformEvents } from "@/lib/chat/use-platform-events";
import { usePlatformEventsContext, usePlatformEventsListener } from "./PlatformEvents";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { RailWidgetSkeleton } from "@/components/ui/states";
import { Avatar } from "@/components/ui/primitives";
import { ProfileCard } from "@/components/casino/ProfileCard";

const STICK_THRESHOLD = 48;

const hueFor = (name: string) => [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

export function ChatPanel({ onClose, side = "right" }: { onClose: () => void; side?: "left" | "right" }) {
  const [roomId, setRoomId] = useState(DEFAULT_ROOM);
  const [draft, setDraft] = useState("");
  const {
    messages,
    status,
    identity,
    minTierOrdinal,
    rooms,
    announcement,
    cooldown,
    idle,
    presence,
    send,
    retry,
    resume,
    refresh,
  } = useChat(roomId);

  /* The platform's event socket still runs headless — it refreshes the feed
     and the balances when something settles — but nothing of it is drawn in
     the panel any more. The shell holds it for every signed-in page
     (PlatformEventsProvider); the panel listens on that one, and opens its own
     only where the shell holds none. */
  const shared = usePlatformEventsContext();
  usePlatformEventsListener(() => {
    if (!idle) refresh();
  });
  usePlatformEvents({
    accountId: identity?.accountId ?? null,
    enabled: !idle && !shared.active,
    onEvent: refresh,
  });

  const [emojiOpen, setEmojiOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const stuckRef = useRef(true);
  const [unseen, setUnseen] = useState(0);

  const scrollToBottom = () => {
    const node = listRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    stuckRef.current = true;
    setUnseen(0);
  };

  useEffect(() => {
    const node = listRef.current;
    if (!node) return;
    if (stuckRef.current) {
      node.scrollTop = node.scrollHeight;
      setUnseen(0);
    } else {
      setUnseen((count) => count + 1);
    }
  }, [messages.length]);

  useEffect(() => {
    stuckRef.current = true;
    setUnseen(0);
  }, [roomId]);

  const onScroll = () => {
    const node = listRef.current;
    if (!node) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    stuckRef.current = distance <= STICK_THRESHOLD;
    if (stuckRef.current) setUnseen(0);
  };

  /* A hidden player has asked not to be publicly identifiable, and posting
     shows their nickname. The first time they try, the composer says so and
     asks; the answer is remembered per account in this browser, and every
     message after a yes carries `reveal` so the server lets it through
     (docs/chat-plan.md §6.3). Nothing is sent until they choose. */
  const revealKey = identity?.accountId ? `ns-chat-reveal:${identity.accountId}` : null;
  const [revealAgreed, setRevealAgreed] = useState(false);
  const [askingReveal, setAskingReveal] = useState(false);

  useEffect(() => {
    if (!revealKey) return;
    try {
      setRevealAgreed(localStorage.getItem(revealKey) === "1");
    } catch {
      setRevealAgreed(false);
    }
  }, [revealKey]);

  const mustAskReveal = Boolean(identity?.isHidden) && !revealAgreed;

  const deliver = (text: string) => {
    void send(text, { reveal: Boolean(identity?.isHidden) });
    setDraft("");
    stuckRef.current = true;
  };

  const submit = () => {
    const text = draft.trim();
    if (!text || cooldown > 0 || tierLocked) return;
    if (mustAskReveal) {
      setAskingReveal(true);
      return;
    }
    deliver(text);
  };

  const agreeToReveal = () => {
    if (revealKey) {
      try {
        localStorage.setItem(revealKey, "1");
      } catch {
        /* Remembered for this session only. */
      }
    }
    setRevealAgreed(true);
    setAskingReveal(false);
    const text = draft.trim();
    if (text && cooldown <= 0) deliver(text);
  };

  /* The panel only exists for a signed-in player (AppShell) and posts as that
     player's platform session, so it never offers to sign in. A guest identity
     here means the session could not be read, not that nobody is signed in. */
  const canPost = Boolean(identity && !identity.isGuest);

  /* The admin's tier gate: signed in, but not far enough up the ladder to
     speak. Reading stays open — only the composer changes. The player's tier
     is read in the browser, over the same session the rest of the page uses,
     so the gate shows even when the chat server cannot see that session; the
     server's own reading only counts when it is higher. The send route
     enforces the same bar server-side. */
  const gated = minTierOrdinal > 0;
  const currentTier = useCurrentTier(gated);
  const tierOrdinal = Math.max(currentTier.data?.ordinal ?? 0, identity?.tierOrdinal ?? 0);
  const tierLocked = gated && Boolean(identity) && tierOrdinal < minTierOrdinal;

  return (
    <aside
      className={cn(
        "flex h-full w-full shrink-0 flex-col bg-elevation-1",
        side === "left" ? "border-r border-border" : "border-l border-border",
      )}
    >
      <div className="flex h-20 shrink-0 items-center gap-[12px] border-b border-border px-[16px]">
        <div className="relative flex h-[38px] min-w-0 flex-1 items-center">
          <select
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            aria-label="Chat room"
            className="h-[38px] w-full cursor-pointer appearance-none rounded-lg bg-elevation-3 pl-[12px] pr-[32px] text-[12px] font-bold leading-[1.6] text-fg outline-none transition-colors hover:bg-elevation-4"
          >
            {(rooms.length ? rooms : [{ id: DEFAULT_ROOM, label: "General Chat" }]).map((room) => (
              <option key={room.id} value={room.id}>
                {room.label}
              </option>
            ))}
          </select>
          <Icon
            name="chevron-down"
            size={16}
            className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-fg"
          />
        </div>
        {presence !== null && (
          <span
            title={`${presence} watching this room`}
            className="flex shrink-0 items-center gap-1 text-[11px] font-bold leading-[1.6] text-fg-subtle"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-success-fg" />
            {presence}
          </span>
        )}
        <button
          onClick={onClose}
          aria-label="Close chat"
          className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg bg-elevation-3 text-fg transition-colors hover:bg-elevation-4"
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      {announcement && (
        <Link
          href={announcement.href}
          className="group flex items-center gap-[4px] border-b border-border px-[16px] py-[12px] text-left transition-colors hover:bg-elevation-2/50"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span className="flex items-center gap-[2px] text-[9px] font-normal leading-[1.5] text-fg-subtle">
              <Icon name="megaphone" size={12} />
              Announcement
            </span>
            <p className="text-[12px] font-bold leading-[1.6] text-fg">{announcement.text}</p>
          </div>
          <Icon
            name="chevron-right"
            size={18}
            className="shrink-0 text-fg-subtle transition-transform group-hover:translate-x-0.5"
          />
        </Link>
      )}

      <ConnectionBar status={status} idle={idle} onResume={resume} />

      <div className="relative min-h-0 flex-1">
        <div
          ref={listRef}
          onScroll={onScroll}
          role="log"
          className="scrollbar-none h-full overflow-y-auto overflow-x-hidden"
        >
          {messages.length === 0 && (
            <p className="px-[16px] py-[24px] text-center text-[12px] leading-[1.6] text-fg-subtle">
              Nobody has said anything yet. Say hello.
            </p>
          )}

          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <ChatRow message={message} onRetry={retry} />
            </motion.div>
          ))}
        </div>

        {unseen > 0 && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-[8px] left-1/2 flex h-[17px] -translate-x-1/2 items-center rounded-rounded bg-brand px-[6px] text-[9px] font-bold leading-[1.5] text-fg-on-primary"
          >
            {unseen} new {unseen === 1 ? "message" : "messages"}
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-border bg-elevation-1">
        {tierLocked && <UnlockRequirement minTierOrdinal={minTierOrdinal} />}
        <div className="p-[16px]">
        {askingReveal && identity && (
          <div
            role="alertdialog"
            aria-labelledby="chat-reveal-title"
            className="mb-[12px] flex flex-col gap-[10px] rounded-md border border-border bg-elevation-2 p-[12px]"
          >
            <p id="chat-reveal-title" className="text-[12px] font-bold leading-[1.6] text-fg">
              Your account is hidden
            </p>
            <p className="text-[11px] leading-[1.6] text-fg-muted">
              Posting shows your nickname <span className="font-bold text-fg">{identity.nickname}</span> to
              everyone in the room. Your hidden setting stays as it is elsewhere.
            </p>
            <div className="flex gap-[8px]">
              <button
                onClick={() => setAskingReveal(false)}
                className="flex h-[30px] flex-1 items-center justify-center rounded-md bg-elevation-3 text-[11px] font-bold text-fg transition-colors hover:bg-elevation-4"
              >
                Keep hidden
              </button>
              <button
                onClick={agreeToReveal}
                className="flex h-[30px] flex-1 items-center justify-center rounded-md bg-brand text-[11px] font-bold text-fg-on-primary transition-colors hover:bg-brand-bold"
              >
                Post as {identity.nickname}
              </button>
            </div>
          </div>
        )}
        {canPost || tierLocked ? (
          <div className="relative">
            {emojiOpen && (
              <EmojiPicker
                onPick={(emoji) => {
                  setDraft((d) => `${d}${emoji}`.slice(0, MAX_BODY_LENGTH));
                  setEmojiOpen(false);
                }}
              />
            )}
            <div className="flex h-[46px] items-center gap-[4px] rounded-md border border-border-input bg-elevation-3 py-[8px] pl-[12px] pr-[8px]">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, MAX_BODY_LENGTH))}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                aria-label="Send a message"
                placeholder={cooldown > 0 ? `Slow down — ${cooldown}s` : "Send a message..."}
                disabled={cooldown > 0 || tierLocked}
                className="h-full min-w-0 flex-1 bg-transparent text-[12px] font-normal leading-[1.6] text-fg outline-none placeholder:text-fg disabled:opacity-50"
              />
              <button
                aria-label="Add emoji"
                aria-expanded={emojiOpen}
                onClick={() => setEmojiOpen((open) => !open)}
                disabled={tierLocked}
                className={cn(
                  "grid h-full w-[30px] shrink-0 place-items-center rounded-sm text-fg transition-colors disabled:opacity-40",
                  emojiOpen ? "bg-elevation-4" : "bg-elevation-2 hover:bg-elevation-4",
                )}
              >
                <Icon name="smile" size={16} />
              </button>
              <button
                aria-label="Send"
                onClick={submit}
                disabled={!draft.trim() || cooldown > 0 || tierLocked}
                className="grid h-full w-[30px] shrink-0 place-items-center rounded-sm bg-brand text-fg-on-primary transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
              >
                <Icon name="send" size={16} />
              </button>
            </div>
            {draft.length > MAX_BODY_LENGTH - 40 && (
              <p className="mt-[4px] text-right text-[9px] leading-[1.5] text-fg-subtle">
                {MAX_BODY_LENGTH - draft.length} left
              </p>
            )}
          </div>
        ) : identity ? (
          <p className="grid h-[46px] place-items-center text-center text-[11px] leading-[1.6] text-fg-subtle">
            Chat is unavailable right now.
          </p>
        ) : (
          <RailWidgetSkeleton className="h-[46px] w-full" />
        )}
        </div>
      </div>
    </aside>
  );
}

/* The kit's chat/unlock-requirement, drawn when the admin's tier gate is
   above the player: a crest and "Reach <tier> to unlock chat" with the
   percentage at the right, a slim progress bar, and the pair of numbers
   underneath. The tier and the numbers are the platform's own — the gate
   tier from the ladder, progress from /incentives/tiers/progress rescaled
   towards it (tierUnlockView). Without numbers to stand behind, the rows
   that state them stay off and the bar sits empty. The composer below stays
   visible but disabled, as the kit draws it; reading is untouched. */
function UnlockRequirement({ minTierOrdinal }: { minTierOrdinal: number }) {
  const bankrolls = useBankrolls(true);
  const ladder = useTierLadder((bankrolls.data ?? []).map((bankroll) => bankroll.id));
  const progress = useTierProgress(bankrolls.data?.at(0)?.id, true);

  const required = (ladder.data ?? []).find((tier) => tier.ordinal >= minTierOrdinal) ?? null;
  const view = tierUnlockView(progress.data ?? null, required);
  const hasNumbers = view.current !== null && view.goal !== null;

  return (
    <div className="flex flex-col gap-[4px] border-b border-border px-[16px] py-[12px]">
      <div className="flex items-center justify-between gap-[4px]">
        <span className="flex min-w-0 items-center gap-[2px]">
          <span className="grid h-[14px] w-[14px] shrink-0 place-items-center overflow-hidden rounded-full bg-elevation-3">
            {required?.imageUrl ? (
              <RemoteImage src={required.imageUrl} alt={required.name} width={14} height={14} />
            ) : (
              <Icon name="lock" size={9} className="text-fg-subtle" />
            )}
          </span>
          <span className="truncate text-[12px] font-bold leading-[1.6] text-fg">
            Reach {required?.name ?? "a higher tier"} to unlock chat
          </span>
        </span>
        {hasNumbers && (
          <span className="shrink-0 text-[12px] font-medium leading-[1.6] text-fg">
            {view.percent.toFixed(2)}%
          </span>
        )}
      </div>
      <div className="flex h-[8px] w-full items-center rounded-rounded bg-elevation-3 px-[2px]">
        <div
          className="h-[4px] rounded-rounded bg-brand"
          style={{ width: `${Math.round(view.percent)}%` }}
        />
      </div>
      {hasNumbers && (
        <div className="flex items-center justify-between gap-[4px] text-[9px] font-medium leading-[1.5] text-fg-subtle">
          <span>{fullNumber(view.current ?? 0)}</span>
          <span>{fullNumber(view.goal ?? 0)}</span>
        </div>
      )}
    </div>
  );
}

const EMOJI = [
  "😀", "😂", "😍", "🤯", "😎", "🤝", "🙌", "👏",
  "🔥", "💎", "🍀", "🎰", "🎲", "🃏", "🚀", "📈",
  "💰", "💸", "🏆", "👑", "⚡", "❤️", "😭", "🤞",
];

function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  return (
    <div className="absolute bottom-11 right-0 z-10 w-[224px] rounded-lg border border-elevation-3 bg-elevation-2 p-2 shadow-xl">
      <div className="grid grid-cols-8 gap-0.5">
        {EMOJI.map((emoji) => (
          <button
            key={emoji}
            onClick={() => onPick(emoji)}
            aria-label={`Insert ${emoji}`}
            className="grid h-6 w-6 place-items-center rounded-[4px] text-[15px] leading-none transition-colors hover:bg-elevation-4"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

function ConnectionBar({
  status,
  idle,
  onResume,
}: {
  status: string;
  idle: boolean;
  onResume: () => void;
}) {
  if (idle) {
    return (
      <button
        onClick={onResume}
        className="flex items-center justify-center gap-[4px] border-b border-border bg-elevation-2 py-[6px] text-[9px] font-bold leading-[1.5] text-fg-muted hover:text-fg"
      >
        <Icon name="refresh" size={12} />
        Chat paused — tap to resume
      </button>
    );
  }

  if (status === "live" || status === "connecting") return null;

  return (
    <div className="flex items-center justify-center gap-[4px] border-b border-border bg-elevation-2 py-[6px] text-[9px] font-bold leading-[1.5] text-fg-muted">
      <span
        className={cn(
          "h-[6px] w-[6px] rounded-full",
          status === "offline" ? "bg-error-fg" : "bg-warning-fg",
        )}
      />
      {status === "offline" ? "Chat disconnected" : "Reconnecting..."}
    </div>
  );
}

export function ChatRow({ message, onRetry }: { message: Row; onRetry: (clientId: string) => void }) {
  const nickname = message.author?.nickname ?? "Flipcoin";

  if (message.deletedAt) {
    return (
      <p className="px-[16px] py-[8px] text-[12px] font-normal italic leading-[1.6] text-fg-subtle">
        Message removed
      </p>
    );
  }

  if (message.kind === "tip") {
    return (
      <div className="flex items-start gap-[2px] px-[16px] py-[8px]">
        <div className="glow-brand-soft flex min-w-0 flex-1 items-center gap-[4px] rounded-md border border-brand bg-elevation-2 p-[8px]">
          <Avatar name={nickname} size={18} />
          <p className="min-w-0 flex-1 break-words text-[12px] font-bold leading-[1.6] text-fg">
            {nickname}
            <span className="font-normal"> tipped </span>
            {message.meta?.recipient ?? "a player"}{" "}
            <span className="text-highlight">{message.meta?.amount}</span>
          </p>
        </div>
      </div>
    );
  }

  if (message.kind === "win") {
    return (
      <div className="flex items-start gap-[2px] px-[16px] py-[8px]">
        <div className="glow-brand-soft flex min-w-0 flex-1 items-center gap-[4px] rounded-md border border-brand bg-elevation-2 p-[8px]">
          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
            <span className="h-[32px] w-[32px] shrink-0 rounded-sm bg-gradient-to-br from-[#8b5cf6] to-[#2d1078]" />
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <p className="line-clamp-2 text-[12px] font-bold leading-[1.6] text-fg">
                {nickname}
                <span className="font-normal"> hit </span>
                <span className="text-highlight">x{message.meta?.multiplier}</span>
                <span className="font-normal"> on </span>
                {message.meta?.gameName}
              </p>
              {message.meta?.hash && (
                <div className="flex items-center gap-[2px]">
                  <p className="truncate text-[8px] font-normal leading-[1.6] text-fg-subtle">{message.meta.hash}</p>
                  <Icon name="link" size={8} className="shrink-0 text-fg-subtle" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (message.kind === "system") {
    const text = message.body.replace(/Terms & Conditions\.?$/, "");
    return (
      <div className="flex items-start gap-[4px] px-[16px] py-[8px]">
        <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-sm bg-brand-fg-accent text-elevation-1">
          <Icon name="shield" size={12} />
        </span>
        <p className="min-w-0 flex-1 break-words text-[12px] font-bold leading-[1.6] text-fg">
          <span className="text-brand-fg-accent">{nickname}:</span>{" "}
          <span className="font-normal text-fg-subtle">{text}</span>
          {message.meta?.href && (
            <Link href={message.meta.href} className="font-normal text-brand-fg-accent underline underline-offset-2">
              Terms &amp; Conditions
            </Link>
          )}
          {message.meta?.href ? <span className="font-normal text-fg-subtle">.</span> : null}
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-start gap-[4px] px-[16px] py-[8px]",
        message.status === "pending" && "opacity-50",
      )}
    >
      <Avatar name={nickname} size={18} />
      <p className="min-w-0 flex-1 break-words text-[12px] font-bold leading-[1.6] text-fg">
        <AuthorName
          name={nickname}
          accountId={message.author?.accountId ?? undefined}
          color={`hsl(${hueFor(nickname)} 70% 72%)`}
        />
        {" "}
        <span className="font-normal text-fg-subtle">{message.body}</span>
        {message.status === "failed" && (
          <button
            onClick={() => message.clientId && onRetry(message.clientId)}
            className="ml-[4px] text-[9px] font-bold leading-[1.5] text-error-fg underline underline-offset-2"
          >
            failed — retry
          </button>
        )}
      </p>
    </div>
  );
}

/* A player's name in the chat opens the same card the live feeds open: a
   name means the same thing wherever it is clicked. Only a real account
   opens one — a system line or a name with no account behind it stays text.
   The card opens upward and at the rail's width, since chat rows sit at the
   bottom of a 256px panel. */
function AuthorName({ name, accountId, color }: { name: string; accountId?: string; color: string }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);

  if (!accountId) {
    return <span style={{ color }}>{name}:</span>;
  }

  return (
    <span ref={setAnchor} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`About ${name}`}
        style={{ color }}
        className="font-bold hover:underline underline-offset-2"
      >
        {name}:
      </button>
      <AnimatePresence>
        {open && (
          <ProfileCard name={name} accountId={accountId} onClose={() => setOpen(false)} side="top" width={224} anchor={anchor} />
        )}
      </AnimatePresence>
    </span>
  );
}
